import { acceptHMRUpdate, defineStore } from 'pinia';
import {
  type CartFragment,
  type SelectedShippingMethodFragment,
  type ShippingCartAddressFragment,
  type BillingCartAddressFragment,
  type SelectedPaymentMethod,
  type CartItemFragment,
  type PlaceQuoteInput
} from '~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';
import { DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { useCartStore } from '~/stores/cartStore/cartStore';
import { createEcommerceTrackingData } from '~/stores/cartStore/helper/createEcommerceTrackingData';
import { G4_EVENT_NAME_PURCHASE } from '~/lib/constants';
import { type ModifyCartItemByDecoratedProduct } from '~/stores/cartStore/model';

export type ConfirmationType = 'request' | 'order';
export enum ConfirmationTypes {
  REQUEST = 'request',
  ORDER = 'order'
}

export const useConfirmationStore = defineStore('shop-confirmation', () => {
  const type = ref<ConfirmationType | null>(null);
  const orderNumber = ref<string | null>(null);
  const email = ref<string | null>(null);
  const shippingMethod = ref<SelectedShippingMethodFragment | null>(null);
  const products = ref<
    ReadonlyArray<CartItemFragment & { decoratedProduct: DecoratedProduct }>
  >([]);
  const prices = ref<CartFragment['prices'] | null>(null);
  const shippingAddress = ref<ShippingCartAddressFragment | null>(null);
  const billingAddress = ref<BillingCartAddressFragment | null>(null);
  const paymentMethod = ref<SelectedPaymentMethod | null>(null);

  const route = useRoute();
  const cartStore = useCartStore();
  const { user } = useOidcAuth();
  const { $globalPageSettings, $analytics, $locale, $textDictionary } =
    useNuxtApp();

  const _state = {
    type,
    orderNumber,
    email,
    shippingMethod,
    products,
    prices,
    shippingAddress,
    billingAddress,
    paymentMethod
  };

  const initByOrder = (givenOrderNumber: string, cart: CartFragment | null) => {
    type.value = ConfirmationTypes.ORDER;
    orderNumber.value = givenOrderNumber;
    email.value = (cart?.email || user.value?.userInfo?.email || null) as
      | string
      | null;
    shippingMethod.value =
      cloneDeep(cart?.shipping_addresses[0]?.selected_shipping_method) ?? null;
    products.value =
      cloneDeep(cart?.itemsV2?.items ?? [])
        .filter(isDefined)
        .map((item) => {
          return {
            ...item,
            decoratedProduct: DecoratedProduct.build(
              item.product,
              $textDictionary.value
            )
          };
        }) ?? [];
    prices.value = cloneDeep(cart?.prices) ?? null;
    shippingAddress.value = cloneDeep(cart?.shipping_addresses[0]) ?? null;
    billingAddress.value = cloneDeep(cart?.billing_address) ?? null;
    paymentMethod.value = cloneDeep(cart?.selected_payment_method) ?? null;
  };

  const initByQuote = (
    cart: CartFragment | null,
    quoteNumber: string,
    input: PlaceQuoteInput
  ) => {
    type.value = ConfirmationTypes.REQUEST;
    orderNumber.value = quoteNumber;
    email.value = input.email ?? null;
    shippingMethod.value = null;
    products.value =
      cloneDeep(cart?.itemsV2?.items ?? [])
        .filter(isDefined)
        .map((item) => {
          return {
            ...item,
            decoratedProduct: DecoratedProduct.build(
              item.product,
              $textDictionary.value
            )
          };
        }) ?? [];
    prices.value = cloneDeep(cart?.prices) ?? null;

    // TODO: Better would be to use a normalized Address Type here
    shippingAddress.value = {
      __typename: 'ShippingCartAddress',
      city: input.city ?? '',
      company: input.company,
      country: {
        code: input.country_id ?? '',
        label: getCountryByCode(input.country_id ?? '')?.countryName ?? ''
      },
      region: { code: input.region_id },
      middlename: input.firstname + ' ' + input.lastname,
      street: [input.street ?? ''],
      postcode: input.postcode,
      telephone: input.telephone,
      same_as_billing: false
    };
    billingAddress.value = null;
    paymentMethod.value = null;
  };

  const tax = computed(() => {
    if (
      isDefined(prices.value) &&
      isDefined(prices.value?.applied_taxes) &&
      Array.isArray(prices.value?.applied_taxes) &&
      isDefined(prices.value?.applied_taxes?.[0])
    ) {
      return prices.value?.applied_taxes?.[0]?.amount;
    }
    return { value: 0 };
  });

  const hasTax = computed(() => {
    return (
      isDefined(tax.value) &&
      isDefined(tax.value?.value) &&
      tax.value?.value > 0
    );
  });

  const hasTariff = computed(() => {
    return prices.value?.fooman_applied_surcharges?.length ?? false;
  });

  const tariff = computed(() => {
    return prices.value?.fooman_applied_surcharges?.[0]?.amount;
  });

  const afterPayedOrder = async function () {
    const confirmationPageSlug =
      $globalPageSettings.value?.shopConfig?.cartConfirmationPage?.slug;

    $analytics.pushToDataLayer({
      event: G4_EVENT_NAME_PURCHASE,
      userId: user?.value?.claims?.oid?.toString() ?? 'null',
      ecommerce: createEcommerceTrackingData({
        cartItems: products.value
          .filter(isDefined)
          .map((item) => {
            return {
              quantity: item.quantity,
              decoratedProduct: item.decoratedProduct
            } as ModifyCartItemByDecoratedProduct;
          })
          .filter(isDefined),
        tax: prices.value?.applied_taxes?.[0]?.amount ?? undefined,
        coupon: prices.value?.discounts?.[0]?.coupon?.code ?? undefined,
        transactionId: orderNumber.value ?? undefined,
        shipping: shippingAddress.value ?? undefined
      })
    });

    cartStore.onOrderCompleted();

    if (confirmationPageSlug) {
      await enhancedNavigateTo(
        {
          path: buildUrlString($locale.value, confirmationPageSlug),
          query: route.query
        },
        {
          external: false,
          replace: false
        }
      );
    }
  };

  const afterSendQuote = async function () {
    const confirmationPageSlug =
      $globalPageSettings.value?.shopConfig?.requestCartConfirmationPage?.slug;

    cartStore.onQuoteCompleted();

    if (confirmationPageSlug) {
      await enhancedNavigateTo(
        {
          path: buildUrlString($locale.value, confirmationPageSlug),
          query: route.query
        },
        {
          external: false,
          replace: false
        }
      );
    }
  };

  const isValid = computed(() => {
    return products.value.length && isDefined(orderNumber.value);
  });

  const afterRequestCompleted = async function () {
    // TODO: implement
  };

  return {
    _state,
    type,
    orderNumber,
    email,
    shippingMethod,
    products,
    prices,
    shippingAddress,
    billingAddress,
    paymentMethod,
    hasTax,
    tax,
    hasTariff,
    tariff,
    isValid,

    initByOrder,
    afterPayedOrder,
    afterRequestCompleted,

    initByQuote,
    afterSendQuote
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useConfirmationStore, import.meta.hot)
  );
}
